import React, { useState, useEffect, useCallback } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import deleteIcon from '../../../shared/assets/svg/cancel_icon.svg';
import Multiselect from 'multiselect-react-dropdown';
import toast from 'react-hot-toast';
import { getAllAccommodationTaskTemplates } from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';

const validationSchema = yup.object().shape({
  task_name: yup.string().required('Task name is required'),
  idleTime: yup.string().required('Idle time is required'),
  duration: yup.string().required('Duration is required'),
  task_ids: yup.array().when('$taskPositionType', {
    is: (val) => val === 'Other',
    then: (schema) => schema.min(1, 'Dependency task required'),
    otherwise: (schema) => schema
  }),
  drawingRequired: yup.string().required('Select if drawing is required'),
  floorwise: yup.string().required('Select Floorwise'),
  unitwise: yup.string().required('Select Unitwise'),
  sections: yup.string().required('Select Sections')
});

const AddEditAccoGroupTaskTemplate = ({
  onClose,
  onSubmit,
  isSubmitting,
  modalType,
  selectedTask,
  setModalType,
  taskPositionType,
  projectId,
  templateName
}) => {
  const userId = Cookies.get('userId');
  const [tasks, setTasks] = useState([]);
  const [subActivityInput, setSubActivityInput] = useState('');
  const [subActivities, setSubActivities] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    context: { taskPositionType }, // 👈 this is important
    defaultValues: {
      task_ids: [],
      sections: 'No'
    }
  });

  console.log('Selected task', selectedTask);

  const {
    data: allTasks,
    refetch: refetchCommonTaskTemplates,
    isFetching
  } = useQuery({
    queryKey: ['getAllAccommodationTaskTemplates', projectId, templateName],
    queryFn: () => getAllAccommodationTaskTemplates('Group', projectId, templateName),
    keepPreviousData: true
  });

  //refetch vendors when component mounts

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Edit Acco Group Task Template Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchCommonTaskTemplates();
  }, []);

  //stare the all vendors in the vendors state
  useEffect(() => {
    if (allTasks?.data) {
      setTasks(allTasks?.data);
    }
  }, [allTasks?.data]);

  // Pre-fill form in edit mode
  useEffect(() => {
    if (modalType !== 'Edit') return;

    if (selectedTask && tasks.length > 0) {
      // Use all tasks for dependency selection (no filtering)
      const allTasks = tasks?.map((item) => ({
        name: item.name?.trim(),
        id: item.id
      }));

      const preSelectedDependencies = allTasks
        .filter((task) => selectedTask.dependencies?.some((d) => d.dependency?.name === task.name))
        .map((task) => ({
          id: task.id,
          name: task.name
        }));

      // 🛡️ DEDUPLICATE BEFORE setting
      const uniqueDependencies = Array.from(
        new Map(preSelectedDependencies.map((dep) => [dep.name, dep])).values()
      );

      setValue('idleTime', selectedTask?.delay || '');
      setValue('task_name', selectedTask?.name || '');
      setValue('duration', selectedTask?.duration || '');
      setValue('area', selectedTask?.area != null ? String(selectedTask.area) : '');
      setSubActivities(selectedTask?.subActivity || []);
      setValue('drawingRequired', selectedTask?.drawing_required ? 'Yes' : 'No');
      setValue('floorwise', selectedTask?.floorwise ? 'Yes' : 'No');
      setValue('unitwise', selectedTask?.unitwise ? 'Yes' : 'No');
      setValue('sections', selectedTask?.sections ? 'Yes' : 'No');
      setValue('task_ids', uniqueDependencies || []);
      setValue('working_order', selectedTask?.working_order || '');
    }
  }, [modalType, selectedTask, tasks, setValue]);

  const onSubmitForm = (formValues) => {
    // if (!formValues.task_ids || formValues.task_ids.length < 1) {
    //   toast.error('Please select dependency task');
    //   return;
    // }

    if (!formValues.working_order && formValues.floorWise === 'Yes') {
      toast.error('Please select working order');
      return;
    }

    if (!formValues.unitwise) {
      toast.error('Please select Unitwise');
      return;
    }

    // Validate floorwise dependency selection
    if (
      formValues.floorwise === 'Yes' &&
      formValues?.task_ids &&
      formValues?.task_ids?.length > 0
    ) {
      const hasFloorwiseTask = formValues.task_ids.some((selectedTask) => {
        const originalTask = tasks.find((task) => task.id === selectedTask.id);
        return originalTask && originalTask.floorwise === true;
      });

      if (!hasFloorwiseTask) {
        toast.error('When Floorwise is Yes, at least one dependency task must be floorwise');
        return;
      }
    }

    // if (!subActivities || subActivities.length < 1) {
    //   toast.error('Please enter subActivity');
    //   return;
    // }
    const payload = {
      name: capitalizeWords(formValues.task_name),
      delay: Number(formValues.idleTime),
      duration: Number(formValues.duration),
      area: formValues.area ? Number(formValues.area) : null,
      dependency: formValues.task_ids?.map((item) => item.name),
      drawing_required: formValues.drawingRequired === 'Yes',
      subActivity: subActivities.map((activity) => capitalizeWords(activity)),
      accomodationType: 'Group',
      floorwise: formValues.floorwise === 'Yes',
      unitwise: formValues.unitwise === 'Yes',
      sections: formValues.sections === 'Yes',
      working_order: formValues.working_order,
      expandable: formValues.floorwise === 'Yes',
      dependencies_ids: formValues.task_ids?.map((item) => item.id),
      template_name: templateName,
      project_id: projectId
    };
    onSubmit(payload);
  };

  const handleIdleNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('idleTime', cleaned);
    },
    [setValue]
  );

  const handleDurationNumberChange = useCallback(
    (val) => {
      let cleaned = val.replace(/[^0-9]/g, '');
      setValue('duration', cleaned);
    },
    [setValue]
  );

  const handleAreaNumberChange = useCallback(
    (val) => {
      // Digits with at most one decimal point, e.g. 1250.5
      const cleaned = val.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
      setValue('area', cleaned);
    },
    [setValue]
  );

  const floorWise = watch('floorwise');
  const unitWise = watch('unitwise');

  // Show all tasks in dependency dropdown
  const filteredTasks = tasks || [];

  const handleAddSubActivity = () => {
    if (subActivityInput.trim() !== '') {
      setSubActivities((prev) => [...prev, capitalizeWords(subActivityInput.trim())]);
      setSubActivityInput('');
    }
  };

  const handleRemoveSubActivity = (index) => {
    setSubActivities((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    console.log('ffffffffffff', errors);
  }, [errors]);

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Task Details</span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label="Task Name"
            register={register}
            register_key="task_name"
            type="text"
            isRequired
            errors={errors.task_name}
          />
          <FormInput
            width={48}
            label="Duration (in days)"
            register={register}
            register_key="duration"
            type="text"
            isRequired
            onType={handleDurationNumberChange}
            errors={errors.duration}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label="Area (sq ft)"
            register={register}
            register_key="area"
            type="text"
            onType={handleAreaNumberChange}
            errors={errors.area}
          />
        </div>
        <div className="flex justify-between items-center w-full flex-wrap gap-3">
          {' '}
          <div className="w-[48%]">
            <label className="text-gray-600 mb-1 block">
              Dependency Task {taskPositionType && taskPositionType === 'Other' ? '*' : ''}
            </label>
            <Controller
              name="task_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={filteredTasks || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected Task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated task:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                  isRequired={taskPositionType && taskPositionType === 'Other'}
                />
              )}
            />
            {errors.task_ids && <span className="text-red-500">{errors.task_ids.message}</span>}
          </div>
          <FormInput
            width={48}
            label="Idle Time (days to wait after dependant tasks)"
            register={register}
            register_key="idleTime"
            type="text"
            onType={handleIdleNumberChange}
            isRequired
            errors={errors.idleTime}
          />
          <div className="flex justify-between flex-col items-start w-[48%] gap-2">
            <label className="text-gray-600" htmlFor="floor">
              Floorwise (repeats the task for each floor) *
            </label>
            <div className="flex justify-start items-center w-[48%] gap-2 space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="floorwise"
                  value="Yes"
                  {...register('floorwise')}
                  onChange={(e) => {
                    setValue('floorwise', e.target.value); // update floorwise
                    setValue('task_ids', []); // reset task_ids
                    setValue('working_order', null); // reset working_order
                  }}
                />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="floorwise"
                  value="No"
                  {...register('floorwise')}
                  onChange={(e) => {
                    setValue('floorwise', e.target.value); // update floorwise
                    setValue('task_ids', []); // reset task_ids
                    setValue('working_order', null); // reset working_order
                  }}
                />
                <span>No</span>
              </label>
            </div>
            {errors.floorwise && <span className="text-red-500">{errors.floorwise.message}</span>}
          </div>
          <div className="flex justify-between flex-col items-start w-[48%] gap-2">
            <label className="text-gray-600" htmlFor="unit">
              Unitwise (repeats the task for each unit) *
            </label>
            <div className="flex justify-start items-center w-[48%] gap-2 space-x-4">
              <label className="flex items-center space-x-1">
                <input type="radio" name="unitwise" value="Yes" {...register('unitwise')} />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-1">
                <input type="radio" name="unitwise" value="No" {...register('unitwise')} />
                <span>No</span>
              </label>
            </div>
            {errors.unitwise && <span className="text-red-500">{errors.unitwise.message}</span>}
          </div>
          {watch('floorwise') === 'Yes' && (
            <FormInput
              width={48}
              label="Working Order"
              register={register}
              register_key="working_order"
              type="dropdown"
              options={[
                {
                  value: '',
                  label: 'Select Working Order',
                  disabled: true,
                  selected: true
                },
                {
                  value: 'Top Down',
                  label: 'Top Down'
                },
                {
                  value: 'Bottom Up',
                  label: 'Bottom Up'
                }
              ]}
              errors={errors.working_order}
              isRequired
            />
          )}
          <div className="flex justify-between flex-col items-start w-[48%] gap-2">
            <label className="text-gray-600" htmlFor="drawing">
              Drawing Required *
            </label>
            <div className="flex justify-start items-center w-[48%] gap-2 space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="drawingRequired"
                  value="Yes"
                  {...register('drawingRequired')}
                />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="drawingRequired"
                  value="No"
                  {...register('drawingRequired')}
                />
                <span>No</span>
              </label>
            </div>
            {errors.drawingRequired && (
              <span className="text-red-500">{errors.drawingRequired.message}</span>
            )}
          </div>
          <div className="flex justify-between flex-col items-start w-[48%] gap-2">
            <label className="text-gray-600" htmlFor="sections">
              Sections *
            </label>
            <div className="flex justify-start items-center w-[48%] gap-2 space-x-4">
              <label className="flex items-center space-x-1">
                <input type="radio" name="sections" value="Yes" {...register('sections')} />
                <span>Yes</span>
              </label>

              <label className="flex items-center space-x-1">
                <input type="radio" name="sections" value="No" {...register('sections')} />
                <span>No</span>
              </label>
            </div>
            {errors.sections && <span className="text-red-500">{errors.sections.message}</span>}
          </div>
          <div className="flex flex-col w-[48%] gap-2">
            <label className="text-gray-600">Subactivity </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={subActivityInput}
                onChange={(e) => setSubActivityInput(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
              <Button
                customStyle={{ padding: '0.3rem 1rem' }}
                type="button"
                onClick={handleAddSubActivity}>
                +
              </Button>
            </div>
            {/* Display subactivities */}
            {subActivities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {subActivities.map((sub, index) => (
                  <div key={index} className="flex items-center bg-gray-200 rounded px-2 py-1">
                    <span>{sub}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubActivity(index)}
                      className="ml-2 text-red-500">
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Submit Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {modalType === 'Add' ? 'Submit' : 'Update'}
          </Button>
        </div>{' '}
      </form>
    </>
  );
};

export default AddEditAccoGroupTaskTemplate;
